import React, { useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from '@mui/material';
import { EmailAuthProvider, reauthenticateWithCredential } from 'firebase/auth';
import { useQueryClient } from 'react-query';
import { useSnackbar } from 'notistack';
import { auth } from '../config/firebase';
import api from '../utils/api';

const DEV_BYPASS = process.env.REACT_APP_DEV_BYPASS_AUTH === 'true';

const digits = (value: string) => value.replace(/\D/g, '').slice(0, 6);

/**
 * Set or change your own PIN (workstation-lock plan T6, D4): one PIN per person, which unlocks a
 * locked screen and, for an owner or admin, approves refunds. The server takes a PIN only within
 * five minutes of a password sign-in, so the password is checked here first. Opened from the user
 * menu and from Settings > Account settings.
 */
const SetPinDialog: React.FC<{ open: boolean; onClose: () => void }> = ({ open, onClose }) => {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const [password, setPassword] = useState('');
  const [pin, setPin] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setPassword('');
    setPin('');
    setConfirm('');
    setError('');
  }, [open]);

  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    if (pin !== confirm) {
      setError("The two PINs don't match.");
      return;
    }
    const current = auth.currentUser;
    // The dev bypass has no Firebase account; its server session already counts as a fresh password.
    if (!DEV_BYPASS && !current?.email) {
      setError('Your sign-in is no longer available here. Please log out and sign back in.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      if (!DEV_BYPASS && current?.email) {
        await reauthenticateWithCredential(current, EmailAuthProvider.credential(current.email, password));
        // The next request must carry the fresh sign-in the server checks.
        await current.getIdToken(true);
      }
      await api.put('/users/me/approval-pin', { pin });
      queryClient.setQueryData(['me-approval-pin'], { hasPin: true });
      enqueueSnackbar('Your PIN is saved.', { variant: 'success' });
      onClose();
    } catch (err: any) {
      const code: string = typeof err?.code === 'string' ? err.code : '';
      if (/wrong-password|invalid-credential|invalid-login-credentials/.test(code)) {
        setError('Incorrect password. Please try again.');
      } else if (code.includes('too-many-requests')) {
        setError('Too many attempts. Please wait a moment and try again.');
      } else if (code.includes('network-request-failed') || (err?.isAxiosError && !err?.response)) {
        setError("Can't reach the server. Try again once you're back online.");
      } else if (err?.response?.data?.code === 'RECENT_PASSWORD_REQUIRED') {
        setError('Enter your password again to set your PIN.');
      } else {
        setError('Could not save your PIN. Try again.');
      }
    } finally {
      setSaving(false);
    }
  };

  const ready = (DEV_BYPASS || password.length > 0) && pin.length === 6 && confirm.length === 6;

  return (
    <Dialog
      open={open}
      onClose={saving ? undefined : onClose}
      maxWidth="xs"
      fullWidth
      aria-labelledby="set-pin-title"
    >
      <form onSubmit={save}>
        <DialogTitle id="set-pin-title">Set your PIN</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Your PIN unlocks a locked screen as you. Owners and admins also use it to approve
            refunds. A new PIN replaces the old one.
          </Typography>
          {!DEV_BYPASS && (
            <TextField
              id="set-pin-password"
              label="Password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
              disabled={saving}
              autoFocus
              fullWidth
              sx={{ mb: 2 }}
            />
          )}
          <TextField
            id="set-pin-new"
            label="New PIN"
            type="password"
            value={pin}
            onChange={(e) => setPin(digits(e.target.value))}
            inputProps={{ inputMode: 'numeric', maxLength: 6, autoComplete: 'new-password' }}
            helperText="6 digits"
            disabled={saving}
            fullWidth
            sx={{ mb: 2 }}
          />
          <TextField
            id="set-pin-confirm"
            label="Confirm PIN"
            type="password"
            value={confirm}
            onChange={(e) => setConfirm(digits(e.target.value))}
            inputProps={{ inputMode: 'numeric', maxLength: 6, autoComplete: 'new-password' }}
            disabled={saving}
            fullWidth
          />
          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={onClose} color="inherit" disabled={saving}>
            Cancel
          </Button>
          <Button type="submit" variant="contained" disabled={saving || !ready}>
            Save PIN
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default SetPinDialog;
